import { postJson, postForm } from './common.js'

// Account

export async function logout() {
  await postJson('/core/user/logout');
}

export async function fetchAccountInfo() {
  const resJson = await postJson('/core/account/info');
  if (!resJson.account) throw Error('Malformed response from backend');
  return resJson.account;
}


export async function emrToPatient(emrIntegrationId) {
  const resJson = await postJson('/core/patient/emr-to-patient', { emrIntegrationId });
  return resJson;
}

// Users

export async function userCheckAuth() {
  const resJson = await postJson('/core/user/check-auth');
  return resJson;
}

export async function listAllUsers() {
  const resJson = await postJson('/core/user/list-all');
  if (!resJson.users) throw Error('Malformed response from backend');
  return resJson.users;
}

export async function updateUserSettings(payload) {
  const resJson = await postJson('/core/user/update-settings', payload);
  if (!resJson.user) throw Error('Malformed response from backend');
  return resJson.user;
}

export async function uploadUserProfilePicture(userId, file) {
  const formBody = new FormData();
  formBody.set('file', file);
  formBody.set('userId', userId);

  const resJson = await postForm('/core/user/upload-profile-picture', formBody);
  if (!resJson.key) throw Error('Malformed response from backend');
  return resJson.key;
}

export async function listInvitations() {
  const resJson = await postJson('/core/userinvitation/list');
  if (!resJson.invitations) throw Error('Malformed response from backend');
  return resJson.invitations;
}

export async function inviteUser(name, email) {
  const resJson = await postJson('/core/userinvitation/create', { name, email });
  if (!resJson.invitation) throw Error('Malformed response from backend');
  return resJson.invitation;
}

export async function deactivateUser(userId) {
  await postJson('/core/user/disable', { userId });
}

export async function renameUser(userId, name) {
  const resJson = await postJson('/core/user/rename', { userId, name });
  if (!resJson.user) throw Error('Malformed response from backend');
  return resJson.user;
}

export async function revokeInvite(invitationId) {
  await postJson('/core/userinvitation/revoke', { invitationId });
}

export async function resendInvite(invitationId) {
  const resJson = await postJson('/core/userinvitation/resend', { invitationId });
  if (!resJson.invitation) throw Error('Malformed response from backend');
  return resJson.invitation;
}

export async function bulkInviteUsers(users) {
  const resJson = await postJson('/core/userinvitation/bulk-create', { users });
  if (!resJson.results) throw Error('Malformed response from backend');
  return resJson;
}

export async function resetUserPassword(userId, newPassword) {
  await postJson('/core/user/reset-password', { userId, newPassword });
}

export async function useOneTimeCode(oneTimeCode) {
  const resJson = await postJson('/core/user/use-otc', { oneTimeCode });
  return resJson;
}

export async function transferOwnership(targetUserId, password) {
  await postJson('/core/user/transfer-ownership', { targetUserId, password });
}

// Patient

export async function createPatient({ firstName, middleName, lastName, preferredName, dateOfBirth, emrIntegrationId, contactMethods }) {
  const resJson = await postJson('/core/patient/create', { firstName, middleName, lastName, preferredName, dateOfBirth, emrIntegrationId, contactMethods });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function addContactMethod({ patientId, methodType, phoneNumber, emailAddress, description }) {
  const resJson = await postJson('/core/patient/add-contact-method', { patientId, methodType, phoneNumber, emailAddress, description });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function editContactMethod({ patientId, methodId, description }) {
  const resJson = await postJson('/core/patient/edit-contact-method', { patientId, methodId, description });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function removeContactMethod({ patientId, methodId }) {
  const resJson = await postJson('/core/patient/remove-contact-method', { patientId, methodId });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function optInContactMethod({ patientId, methodId, isOptedIn }) {
  const resJson = await postJson('/core/patient/opt-in-contact-method', { patientId, methodId, isOptedIn });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function updatePatient(patientId, fieldsToUpdate) {
  const resJson = await postJson('/core/patient/update', { patientId, ...fieldsToUpdate });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function searchPatients({ name, phone, email, afterPatientId }) {
  let requestBody = {};
  if (name) requestBody.name = name;
  if (phone) requestBody.phone = phone;
  if (email) requestBody.email = email;
  if (afterPatientId) requestBody.afterPatientId = afterPatientId;
  const resJson = await postJson('/core/patient/search', requestBody);
  if (!resJson.patients) throw Error('Malformed response from backend');
  return resJson;
}

export async function fetchPatientInfo(patientId) {
  const resJson = await postJson('/core/patient/info', { patientId });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return resJson.patient;
}

export async function uploadPatientProfilePicture(patientId, file) {
  const formBody = new FormData();
  formBody.set('file', file);
  formBody.set('patientId', patientId);

  const resJson = await postForm('/core/patient/upload-profile-picture', formBody);
  if (!resJson.key) throw Error('Malformed response from backend');
  return resJson.key;
}

export async function archivePatient(patientId) {
  await postJson('/core/patient/archive', { patientId });
}

export async function mergePatients(patientIds) {
  const resJson = await postJson('/core/patient/merge-patients', { patientIds });
  return resJson;
}

export async function updateEmrIntegration(patientId, emrIntegrationId, disconnectExisting = false) {
  const resJson = await postJson('/core/patient/update-emr-integration', { patientId, emrIntegrationId, disconnectExisting });
  if (!resJson.patient) throw Error('Malformed response from backend');
  return { patient: resJson.patient, disconnectedPatientId: resJson.disconnectedPatientId || null };
}

// Returns the patient currently holding this EMR integration id, or null when it is free
export async function lookupEmrIntegration(emrIntegrationId) {
  const resJson = await postJson('/core/patient/lookup-emr-integration', { emrIntegrationId });
  return resJson.patient || null;
}


// Settings

export async function getFaxPhone() {
  const resJson = await postJson('/core/phone/get-fax-phone');
  return resJson.faxPhones || [];
}

export async function setFaxPhone(faxPhones) {
  const resJson = await postJson('/core/phone/set-fax-phone', { faxPhones });
  return resJson.faxPhones || [];
}

export async function getAccountSettings() {
  const resJson = await postJson('/core/account/get-settings');
  if (!resJson.settings) throw Error('Malformed response from backend');
  return resJson;
}

export async function setAccountSettings(payload) {
  const resJson = await postJson('/core/account/set-settings', payload);
  if (!resJson.settings) throw Error('Malformed response from backend');
  return resJson;
}

export async function getUserSettings(userId) {
  const resJson = await postJson('/core/user/get-settings', { userId });
  if (!resJson.settings) throw Error('Malformed response from backend');
  return resJson.settings;
}

export async function setUserSettings(payload) {
  const resJson = await postJson('/core/user/set-settings', payload);
  if (!resJson.settings) throw Error('Malformed response from backend');
  return resJson.settings;
}

// BUSINESS

export async function searchBusinesses(query, afterBusinessId) {
  const resJson = await postJson('/core/business/search', { query, afterBusinessId });
  if (!resJson.businesses) throw Error('Malformed response from backend');
  return resJson;
}

export async function createBusinesss({ name, faxNumber, email }) {
  const resJson = await postJson('/core/business/create', { name, faxNumber, email });
  if (!resJson.business) throw Error('Malformed response from backend');
  return resJson.business;
}

export async function updateBusiness(businessId, fieldsToUpdate) {
  const resJson = await postJson('/core/business/update', { businessId, ...fieldsToUpdate });
  if (!resJson.business) throw Error('Malformed response from backend');
  return resJson.business;
}

export async function archiveBusiness(businessId) {
  await postJson('/core/business/archive', { businessId });
}

export async function bulkArchiveBusinesses(businessIds) {
  const resJson = await postJson('/core/business/bulk-archive', { businessIds });
  return resJson;
}

export async function bulkCreateBusinesses(businesses) {
  const resJson = await postJson('/core/business/bulk-create', { businesses });
  if (!resJson.results) throw Error('Malformed response from backend');
  return resJson;
}

export async function getOrCreateBusiness({ faxNumber, name }) {
  const body = { faxNumber };
  if (name) body.name = name;
  const resJson = await postJson('/core/business/get-or-create', body);
  if (!resJson.business) throw Error('Malformed response from backend');
  return resJson.business;
}

export async function lookupPatient({ phoneNumber, emailAddress }) {
  const resJson = await postJson('/core/patient/lookup', { phoneNumber, emailAddress });
  return resJson;
}
